import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO, { SITE_URL } from "@/components/SEO";
import NotFound from "@/pages/NotFound";
import { BlogBlock } from "@/blog/BlogBlocks";
import { markPrerenderReady } from "@/lib/seo";
import { trackEvent } from "@/blog/track";
import { getFeature } from "@/features";

export default function FeaturePage({ slug: slugProp }) {
  const params = useParams();
  const feature = getFeature(slugProp || params.slug);

  useEffect(() => {
    if (feature) markPrerenderReady();
  }, [feature]);

  if (!feature) {
    return (
      <>
        <Helmet>
          <title>Page not found | Checkout DM</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="prerender-status-code" content="404" />
        </Helmet>
        <NotFound />
      </>
    );
  }

  const url = `${SITE_URL}${feature.path}`;
  const isCompare = feature.slug === "compare";

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: feature.h1,
      description: feature.description,
      url,
      isPartOf: { "@type": "WebSite", name: "Checkout DM", url: SITE_URL },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: isCompare
        ? [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Compare", item: url },
          ]
        : [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Features", item: `${SITE_URL}/features` },
            { "@type": "ListItem", position: 3, name: feature.eyebrow, item: url },
          ],
    },
  ];
  if (feature.faqs?.length) {
    schema.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: feature.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  const track = (destination, location) =>
    trackEvent("feature_cta_click", { feature: feature.slug, cta_location: location, destination });

  return (
    <>
      <SEO
        title={feature.seoTitle}
        description={feature.description}
        path={feature.path}
        structuredData={schema}
      />
      <Navbar />

      <header className="px-6 pb-8 pt-12 md:px-12 md:pt-20">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="text-sm text-ink/55">
            <Link to="/" className="hover:text-hotpink">Home</Link>
            <span className="mx-2">/</span>
            {isCompare ? (
              <span>Compare</span>
            ) : (
              <>
                <Link to="/features" className="hover:text-hotpink">Features</Link>
                <span className="mx-2">/</span>
                <span>{feature.eyebrow}</span>
              </>
            )}
          </nav>

          <span className="mt-8 inline-block rounded-full bg-lilac px-3 py-1 text-xs font-bold text-ink">
            {feature.eyebrow}
          </span>
          <h1
            data-testid="feature-title"
            className="mt-5 max-w-4xl text-balance font-display text-4xl leading-[0.98] tracking-tighter md:text-6xl"
          >
            {feature.h1}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink/70">{feature.intro}</p>

          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Link
              to="/login"
              onClick={() => track("/login", "hero")}
              className="cta-glow rounded-full bg-sunset-gradient px-6 py-3 text-sm font-semibold text-white"
            >
              Connect Instagram
            </Link>
            <Link
              to="/pricing"
              onClick={() => track("/pricing", "hero")}
              className="text-sm font-semibold text-ink/80 hover:text-hotpink"
            >
              See pricing
            </Link>
          </div>

          {feature.glance?.length > 0 && (
            <dl className="mt-10 grid gap-3 sm:grid-cols-3">
              {feature.glance.map((g) => (
                <div key={g.label} className="rounded-2xl border border-ink/10 bg-white p-4">
                  <dt className="text-xs font-bold uppercase tracking-[0.15em] text-ink/50">{g.label}</dt>
                  <dd className="mt-1.5 font-display text-[17px] font-bold leading-snug text-ink">{g.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </header>

      <div className="px-6 pb-24 pt-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          <article data-testid="feature-article" className="min-w-0">
            {feature.blocks.map((block, i) => (
              <BlogBlock key={i} block={block} />
            ))}
          </article>

          {feature.related?.length > 0 && (
            <section className="mt-14" aria-labelledby="related-pages">
              <h2 id="related-pages" className="font-display text-2xl leading-tight tracking-tight">
                Keep exploring
              </h2>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {feature.related.map((r) => (
                  <li key={r.to}>
                    <Link
                      to={r.to}
                      onClick={() => track(r.to, "related")}
                      className="group flex h-full flex-col rounded-2xl border border-ink/10 bg-white p-5 transition-colors hover:border-hotpink/50"
                    >
                      <span className="flex items-center justify-between gap-3 font-display text-[17px] font-bold text-ink">
                        {r.title}
                        <ArrowUpRight size={18} className="flex-none text-ink/40 transition-colors group-hover:text-hotpink" aria-hidden="true" />
                      </span>
                      <span className="mt-1.5 text-[14px] leading-relaxed text-ink/60">{r.description}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {feature.faqs?.length > 0 && (
            <section className="mt-16" aria-labelledby="faq">
              <h2 id="faq" className="scroll-mt-28 font-display text-[1.75rem] leading-[1.1] tracking-tight md:text-[2.1rem]">
                Frequently asked questions
              </h2>
              <div className="mt-6 divide-y divide-ink/10 rounded-3xl border border-ink/10 bg-white">
                {feature.faqs.map((f) => (
                  <details key={f.q} className="group px-6 py-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-lg font-bold text-ink">
                      {f.q}
                      <span aria-hidden="true" className="text-2xl leading-none text-hotpink transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 text-[16px] leading-relaxed text-ink/70">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          <aside className="mt-16 rounded-[2rem] bg-ink p-8 text-cream md:p-10" data-testid="feature-cta">
            <h2 className="font-display text-3xl leading-[1.05] tracking-tight md:text-4xl">
              Turn your DMs into checkouts.
            </h2>
            <p className="mt-3 max-w-md text-[16px] leading-relaxed text-cream/70">
              Connect your Instagram account and set up your catalog in minutes. The Free plan
              includes 5 products and 15 customer conversations a month.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Link
                to="/login"
                onClick={() => track("/login", "page_end")}
                className="cta-glow rounded-full bg-sunset-gradient px-6 py-3 text-sm font-semibold text-white"
              >
                Connect Instagram
              </Link>
              <Link
                to="/pricing"
                onClick={() => track("/pricing", "page_end")}
                className="text-sm font-semibold text-cream/80 hover:text-white"
              >
                See pricing
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </>
  );
}
